// The drawn marks of the casino (casino.md §16.1, R2165).
//
// The slot symbols were text glyphs, and the kraken ❦ read as a floral
// mark (the critique of 2026-10-13). Mockup round 101 card 04 drew each
// symbol as a stroke mark in a round rim, and the rim takes the club ink:
// the symbols stand in the club. The lobby rows draw one mark a game in
// the same ink (the mess board of card 01).
//
// **Every mark is built ONE time** (R988). A mark is a static string kept
// in a memo, and the club ink reaches it through the classes `sm-rim` and
// `gm` of the casino sheet, which read `--club`. A change of the club
// therefore writes one property on the host and rebuilds no mark, and a
// render pass at 4 Hz pays a map lookup.
//
// **The symbols are indices, never words.** `CasinoResult.reels` carries
// one index per reel into `CASINO_SYMBOLS` of `data/contracts/casino.toml`.
// The name of each symbol is a key of `gen/strings.ts`, so a screen reader
// reads the word, and a sentence names the symbol by that word.
//
// The geometry stands in a box of 24 units for a symbol and of 28 units
// for a game mark, centred, so a mark scales to any size the sheet gives
// it. The paths are the marks of the mockup, by hand; no figure of them is
// a game or presentation figure.

import { CASINO_SYMBOLS } from '../../gen/balance';
import { overlay as S } from '../../gen/strings';
import { CasinoGame } from '../../gen/proto/aquaphobia';

/** One symbol: its strokes and its filled dots (cx, cy, r). */
interface Mark {
  strokes: readonly string[];
  dots: readonly (readonly [number, number, number])[];
  /** The index of a stroke that closes a body, which takes the fill ink. */
  body?: number;
}

/** The five symbols and the blank, by the wire id of `CASINO_SYMBOLS`. */
const MARKS: Readonly<Record<string, Mark>> = {
  kraken: {
    strokes: [
      'M8 12C6 9 7 3.5 12 3.5S18 9 16 12',
      'M8 12C6.4 14 3.5 13.4 3.5 16.4C3.5 18.6 6 18.7 6.4 16.8',
      'M10.3 12.6C10 16 8 17.6 8.7 20.6',
      'M13.7 12.6C14 16 16 17.6 15.3 20.6',
      'M16 12C17.6 14 20.5 13.4 20.5 16.4C20.5 18.6 18 18.7 17.6 16.8',
    ],
    dots: [[10.1, 9.2, 1.05], [13.9, 9.2, 1.05]],
  },
  trident: {
    strokes: [
      'M12 21.5V2.6',
      'M7 3.6V8.6C7 10.6 8.8 11.3 12 11.3S17 10.6 17 8.6V3.6',
      'M5.6 5.1L7 3.3L8.4 5.1',
      'M15.6 5.1L17 3.3L18.4 5.1',
      'M10.6 4.3L12 2.5L13.4 4.3',
      'M10.2 21.5H13.8',
    ],
    dots: [],
  },
  pearl: {
    strokes: [
      'M17.2 12.5A5.2 5.2 0 1 1 6.8 12.5A5.2 5.2 0 1 1 17.2 12.5Z',
      'M9.4 11.4A2.9 2.9 0 0 1 11.3 9.4',
      'M4.4 18.4C8 21 16 21 19.6 18.4',
    ],
    dots: [],
    body: 0,
  },
  shell: {
    strokes: [
      'M12 19L5 10.6C4.6 6.3 8.2 3.6 12 3.6S19.4 6.3 19 10.6Z',
      'M12 19L8.4 4.4',
      'M12 19V3.6',
      'M12 19L15.6 4.4',
      'M12 19L5.5 7.6',
      'M12 19L18.5 7.6',
      'M9.8 19H14.2L13.4 21H10.6Z',
    ],
    dots: [],
  },
  anchor: {
    strokes: [
      'M13.9 4.4A1.9 1.9 0 1 1 10.1 4.4A1.9 1.9 0 1 1 13.9 4.4Z',
      'M12 6.3V20.5',
      'M8.2 9.2H15.8',
      'M4.8 14.8C5.6 18.8 8.3 20.5 12 20.5S18.4 18.8 19.2 14.8',
      'M3.4 16.4L4.8 14.6L6.8 15.4',
      'M20.6 16.4L19.2 14.6L17.2 15.4',
    ],
    dots: [],
  },
  blank: { strokes: ['M8.5 12H15.5'], dots: [] },
};

/** The word of each symbol, in the reader's language (R855). */
const NAMES: Readonly<Record<string, string>> = {
  kraken: S.slots_sym_kraken,
  trident: S.slots_sym_trident,
  pearl: S.slots_sym_pearl,
  shell: S.slots_sym_shell,
  anchor: S.slots_sym_anchor,
  blank: S.slots_sym_blank,
};

/** The wire id of one symbol index. An index the pack does not name is blank. */
function idOf(i: number): string {
  const id = CASINO_SYMBOLS[i] as string | undefined;
  return id !== undefined && MARKS[id] ? id : 'blank';
}

/** The word of one symbol index: the name a sentence and a reader take. */
export function symbolName(i: number): string {
  return NAMES[idOf(i)] ?? S.slots_sym_blank;
}

/** Every built symbol, by its id. One build a session. */
const BUILT = new Map<string, string>();

/**
 * One symbol as an inline SVG in its club rim. The blank stop draws no
 * rim, because it is no symbol and the club owns none of it.
 */
function build(id: string): string {
  const m = MARKS[id] ?? MARKS.blank;
  const blank = id === 'blank';
  const strokes = m.strokes.map((d, i) => `<path${i === m.body ? ' class="sm-body"' : ''} d="${d}"/>`).join('');
  const dots = m.dots.map(([x, y, r]) => `<circle class="sm-dot" cx="${x}" cy="${y}" r="${r}"/>`).join('');
  return `<svg class="sm${blank ? ' blank' : ''}" viewBox="0 0 24 24" role="img" aria-label="${NAMES[id] ?? id}" focusable="false">`
    + (blank ? '' : '<circle class="sm-rim" cx="12" cy="12" r="11.2"/>')
    + `<g class="sm-ink">${strokes}</g>${dots}</svg>`;
}

/** One symbol index as its drawn mark, built once (R988). */
export function symbolMark(i: number): string {
  const id = idOf(i);
  let svg = BUILT.get(id);
  if (svg === undefined) {
    svg = build(id);
    BUILT.set(id, svg);
  }
  return svg;
}

/**
 * The mark a paytable row draws where it takes any symbol at all: the club
 * rim, dashed, with no symbol in it.
 */
export const ANY_MARK = `<svg class="sm any" viewBox="0 0 24 24" role="img" aria-label="${S.slots_sym_any}" focusable="false">`
  + '<circle class="sm-rim" cx="12" cy="12" r="11.2"/></svg>';

// --- the game marks of the lobby rows (card 01, the mess board) --------

/** One point on a circle of the 28-unit box, at an angle in radians. */
function at(r: number, a: number): string {
  return `${(14 + r * Math.cos(a)).toFixed(1)} ${(14 + r * Math.sin(a)).toFixed(1)}`;
}

/** The wheel: a rim, a hub, twelve spokes and the ball at the top. */
function rouletteMark(): string {
  let spokes = '';
  for (let i = 0; i < 12; i++) {
    const a = (i * Math.PI) / 6;
    spokes += `<path d="M${at(8, a)}L${at(11, a)}"/>`;
  }
  return '<circle cx="14" cy="14" r="11"/><circle cx="14" cy="14" r="5"/>'
    + `${spokes}<circle class="gm-dot" cx="14" cy="5.5" r="1.4"/>`;
}

/** Hold'em: the oval table and the six casino seats round it. */
function holdemMark(): string {
  let seats = '';
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3 + Math.PI / 6;
    seats += `<circle class="gm-dot" cx="${(14 + 12 * Math.cos(a)).toFixed(1)}"`
      + ` cy="${(14 + 8 * Math.sin(a)).toFixed(1)}" r="1.6"/>`;
  }
  return `<ellipse cx="14" cy="14" rx="9" ry="5.5"/>${seats}`;
}

/** The four game marks, built once at load. */
const GAME_MARKS: Readonly<Partial<Record<CasinoGame, string>>> = {
  [CasinoGame.CASINO_GAME_ROULETTE]: rouletteMark(),
  [CasinoGame.CASINO_GAME_BLACKJACK]:
    '<rect x="5" y="6" width="11" height="16" rx="1.5" transform="rotate(-10 10 14)"/>'
    + '<rect x="12" y="6" width="11" height="16" rx="1.5" transform="rotate(8 17 14)"/>',
  [CasinoGame.CASINO_GAME_SLOTS]:
    '<rect x="3" y="6" width="22" height="16" rx="2"/>'
    + '<path class="gm-dash" d="M10.3 6V22M17.7 6V22M3 14H25"/>',
  [CasinoGame.CASINO_GAME_HOLDEM]: holdemMark(),
};

/** The drawn mark of one game, in the club ink of the sheet. Decoration:
 *  the row names the game in words beside it. */
export function gameMark(game: CasinoGame): string {
  const inner = GAME_MARKS[game] ?? '';
  return `<svg class="gm" viewBox="0 0 28 28" aria-hidden="true" focusable="false">${inner}</svg>`;
}
